(() => {
  const WEEKDAY_KEYS = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const ORDER_WEEKDAY_KEYS = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const WEEKDAY_LABELS = {
    sunday: "Su",
    monday: "Mo",
    tuesday: "Tu",
    wednesday: "We",
    thursday: "Th",
    friday: "Fr",
    saturday: "Sa",
  };

  const ISO_DATE_RE = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
  const TIME_24H_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
  const TIME_AMPM_RE = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*([ap]m)$/i;
  const TARGET_GID_RE = /^gid:\/\/shopify\/(Product|Collection)\/(\d+)$/;
  const DEFAULT_CALENDAR_NOTES_TEXT = "";
  const DEFAULT_DELIVERY_WEEKDAYS = [
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const DEFAULT_SETTINGS = {
    timezone: "Europe/London",
    firstDayOfWeek: "monday",
    maxDeliveryDays: 30,
    cutoff: {
      enabled: true,
      time: "12:00",
    },
    minIntervalByOrderWeekday: {
      monday: 1,
      tuesday: 1,
      wednesday: 1,
      thursday: 1,
      friday: 1,
      saturday: 1,
      sunday: 1,
    },
    deliveryWeekdays: DEFAULT_DELIVERY_WEEKDAYS,
    blockoutDates: [],
    events: [],
    soldOutDates: [],
    notesText: DEFAULT_CALENDAR_NOTES_TEXT,
    availability: {
      enabled: false,
      targetType: "specific_products",
      cartMatchMode: "all",
      dateMode: "allow",
      productRules: [],
      collectionRules: [],
    },
    ui: {
      showLegend: true,
      calendarAlignment: "center",
      selectedDateColor: "#fa1b3c",
      availableDateColor: "#ffe9f5",
      soldOutDateColor: "#D9D9D9",
      unavailableDateColor: "#F3F3F3",
      eventTagColor: "#fffdc2",
      todayDotColor: "#7f0016",
    },
    enforcement: {
      disableCalendarForSelectedProducts: false,
      calendarDisabledProducts: [],
    },
  };

  function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function parseJsonValue(value) {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return value;

    try {
      return JSON.parse(trimmed);
    } catch (_error) {
      return value;
    }
  }

  function clampInt(value, fallback, min, max) {
    const parsed = Number.parseInt(String(value), 10);
    if (Number.isNaN(parsed)) return fallback;
    return Math.max(min, Math.min(max, parsed));
  }

  function pad2(value) {
    return String(value).padStart(2, "0");
  }

  function normalizeWeekday(value, fallback) {
    if (typeof value !== "string") return fallback;
    const normalized = value.trim().toLowerCase();
    return WEEKDAY_KEYS.includes(normalized) ? normalized : fallback;
  }

  function normalizeIsoDate(value) {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    const match = trimmed.match(ISO_DATE_RE);
    if (!match) return null;

    const year = Number.parseInt(match[1], 10);
    const month = Number.parseInt(match[2], 10);
    const day = Number.parseInt(match[3], 10);

    const utcDate = new Date(Date.UTC(year, month - 1, day));
    if (
      utcDate.getUTCFullYear() !== year ||
      utcDate.getUTCMonth() + 1 !== month ||
      utcDate.getUTCDate() !== day
    ) {
      return null;
    }

    return trimmed;
  }

  function normalizeTime(value, fallback) {
    if (typeof value !== "string") return fallback;
    const trimmed = value.trim();

    if (TIME_24H_RE.test(trimmed)) {
      return trimmed;
    }

    const ampmMatch = trimmed.match(TIME_AMPM_RE);
    if (!ampmMatch) return fallback;

    const hour12 = Number.parseInt(ampmMatch[1], 10);
    const minute = ampmMatch[2];
    const suffix = ampmMatch[3].toLowerCase();
    const hour24 = suffix === "pm" ? (hour12 % 12) + 12 : hour12 % 12;

    return `${pad2(hour24)}:${minute}`;
  }

  function normalizeCalendarNoticeText(value) {
    if (typeof value !== "string") return "";
    const trimmed = value.replace(/\r\n/g, "\n").trim();
    return trimmed;
  }

  function splitCalendarNotes(value) {
    const normalized = normalizeCalendarNoticeText(value);
    if (!normalized) return [];

    return normalized
      .split(/\n\s*\n+/)
      .map((entry) => entry.trim())
      .filter((entry) => Boolean(entry));
  }

  function normalizeNotesText(source) {
    return normalizeCalendarNoticeText(source.notesText);
  }

  function normalizeTargetId(value, expectedType) {
    if (typeof value === "number") return String(value);
    if (typeof value !== "string") return "";

    const trimmed = value.trim();
    if (!trimmed) return "";

    const gidMatch = trimmed.match(TARGET_GID_RE);
    if (!gidMatch) return trimmed;

    if (expectedType && gidMatch[1] !== expectedType) {
      return "";
    }

    return gidMatch[2];
  }

  function normalizeDateList(value) {
    if (!Array.isArray(value)) return [];

    const normalized = value
      .map((entry) => normalizeIsoDate(entry))
      .filter((entry) => Boolean(entry));

    return Array.from(new Set(normalized)).sort((left, right) => left.localeCompare(right));
  }

  function normalizeEvents(value) {
    if (!Array.isArray(value)) return [];

    const events = value
      .map((entry) => {
        if (!isRecord(entry)) return null;

        const date = normalizeIsoDate(entry.date);
        const label = typeof entry.label === "string" ? entry.label.trim() : "";

        if (!date || !label) return null;

        return {
          date,
          label: label.slice(0, 60),
        };
      })
      .filter((entry) => Boolean(entry))
      .sort((left, right) => {
        if (left.date === right.date) return left.label.localeCompare(right.label);
        return left.date.localeCompare(right.date);
      });

    const seen = new Set();
    return events.filter((entry) => {
      const key = `${entry.date}:${entry.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function normalizeWeekdayMap(value, fallbackMap) {
    const source = isRecord(value) ? value : {};
    const normalized = {};

    ORDER_WEEKDAY_KEYS.forEach((weekday) => {
      normalized[weekday] = clampInt(source[weekday], fallbackMap[weekday], 0, 60);
    });

    return normalized;
  }

  function normalizeDeliveryWeekdays(value, fallback) {
    if (!Array.isArray(value)) return fallback;

    const normalized = value
      .map((entry) => (typeof entry === "string" ? normalizeWeekday(entry, "") : ""))
      .filter((entry, index, all) => Boolean(entry) && all.indexOf(entry) === index);

    return normalized.length ? normalized : fallback;
  }

  function normalizeAlignment(value) {
    if (value === "left" || value === "right") return value;
    return "center";
  }

  function normalizeAvailabilityRule(value, expectedType) {
    if (!isRecord(value)) return null;

    const id = normalizeTargetId(value.id, expectedType);
    if (!id) return null;

    return {
      id,
      title: typeof value.title === "string" ? value.title.trim() : "",
      handle: typeof value.handle === "string" ? value.handle.trim() : "",
      imageUrl:
        typeof value.imageUrl === "string" && value.imageUrl.trim() ? value.imageUrl.trim() : null,
      dates: normalizeDateList(value.dates),
    };
  }

  function normalizeAvailabilityRules(value, expectedType) {
    if (!Array.isArray(value)) return [];

    const byId = new Map();
    value.forEach((entry) => {
      const normalized = normalizeAvailabilityRule(entry, expectedType);
      if (!normalized) return;
      byId.set(normalized.id, normalized);
    });

    return Array.from(byId.values());
  }

  function normalizeProductReference(value, expectedType) {
    if (!isRecord(value)) return null;

    const id = normalizeTargetId(value.id, expectedType);
    if (!id) return null;

    return {
      id,
      title: typeof value.title === "string" ? value.title.trim() : "",
      handle: typeof value.handle === "string" ? value.handle.trim() : "",
      imageUrl:
        typeof value.imageUrl === "string" && value.imageUrl.trim() ? value.imageUrl.trim() : null,
    };
  }

  function normalizeProductReferenceList(value, expectedType) {
    if (!Array.isArray(value)) return [];

    const byId = new Map();
    value.forEach((entry) => {
      const normalized = normalizeProductReference(entry, expectedType);
      if (!normalized) return;
      byId.set(normalized.id, normalized);
    });

    return Array.from(byId.values());
  }

  function normalizeAvailability(value) {
    const source = isRecord(value) ? value : null;
    if (!source) return DEFAULT_SETTINGS.availability;

    return {
      enabled:
        typeof source.enabled === "boolean"
          ? source.enabled
          : DEFAULT_SETTINGS.availability.enabled,
      targetType:
        source.targetType === "specific_collections"
          ? "specific_collections"
          : "specific_products",
      cartMatchMode: source.cartMatchMode === "any" ? "any" : "all",
      dateMode: source.dateMode === "deny" ? "deny" : "allow",
      productRules: normalizeAvailabilityRules(source.productRules, "Product"),
      collectionRules: normalizeAvailabilityRules(source.collectionRules, "Collection"),
    };
  }

  function normalizeSettings(value) {
    const parsed = parseJsonValue(value);
    const source = isRecord(parsed) ? parsed : {};

    const cutoff = isRecord(source.cutoff) ? source.cutoff : {};
    const notesText = normalizeNotesText(source);
    const ui = isRecord(source.ui) ? source.ui : {};
    const enforcement = isRecord(source.enforcement) ? source.enforcement : {};
    const availability = normalizeAvailability(source.availability);
    const normalizedFirstDay = normalizeWeekday(source.firstDayOfWeek, DEFAULT_SETTINGS.firstDayOfWeek);
    const deliveryWeekdays = normalizeDeliveryWeekdays(
      source.deliveryWeekdays,
      DEFAULT_SETTINGS.deliveryWeekdays,
    );

    return {
      timezone:
        typeof source.timezone === "string" && source.timezone.trim()
          ? source.timezone.trim()
          : DEFAULT_SETTINGS.timezone,
      firstDayOfWeek: normalizedFirstDay,
      maxDeliveryDays: clampInt(source.maxDeliveryDays, DEFAULT_SETTINGS.maxDeliveryDays, 1, 120),
      cutoff: {
        enabled:
          typeof cutoff.enabled === "boolean" ? cutoff.enabled : DEFAULT_SETTINGS.cutoff.enabled,
        time: normalizeTime(cutoff.time, DEFAULT_SETTINGS.cutoff.time),
      },
      minIntervalByOrderWeekday: normalizeWeekdayMap(
        source.minIntervalByOrderWeekday,
        DEFAULT_SETTINGS.minIntervalByOrderWeekday,
      ),
      deliveryWeekdays,
      blockoutDates: normalizeDateList(source.blockoutDates),
      events: normalizeEvents(source.events),
      soldOutDates: normalizeDateList(source.soldOutDates),
      notesText,
      availability,
      ui: {
        showLegend:
          typeof ui.showLegend === "boolean" ? ui.showLegend : DEFAULT_SETTINGS.ui.showLegend,
        calendarAlignment: normalizeAlignment(ui.calendarAlignment),
        selectedDateColor:
          typeof ui.selectedDateColor === "string" && ui.selectedDateColor.trim()
            ? ui.selectedDateColor.trim()
            : DEFAULT_SETTINGS.ui.selectedDateColor,
        availableDateColor:
          typeof ui.availableDateColor === "string" && ui.availableDateColor.trim()
            ? ui.availableDateColor.trim()
            : DEFAULT_SETTINGS.ui.availableDateColor,
        soldOutDateColor:
          typeof ui.soldOutDateColor === "string" && ui.soldOutDateColor.trim()
            ? ui.soldOutDateColor.trim()
            : DEFAULT_SETTINGS.ui.soldOutDateColor,
        unavailableDateColor:
          typeof ui.unavailableDateColor === "string" && ui.unavailableDateColor.trim()
            ? ui.unavailableDateColor.trim()
            : DEFAULT_SETTINGS.ui.unavailableDateColor,
        eventTagColor:
          typeof ui.eventTagColor === "string" && ui.eventTagColor.trim()
            ? ui.eventTagColor.trim()
            : DEFAULT_SETTINGS.ui.eventTagColor,
        todayDotColor:
          typeof ui.todayDotColor === "string" && ui.todayDotColor.trim()
            ? ui.todayDotColor.trim()
            : DEFAULT_SETTINGS.ui.todayDotColor,
      },
      enforcement: {
        disableCalendarForSelectedProducts: Boolean(
          enforcement.disableCalendarForSelectedProducts ??
            DEFAULT_SETTINGS.enforcement.disableCalendarForSelectedProducts,
        ),
        calendarDisabledProducts: normalizeProductReferenceList(
          enforcement.calendarDisabledProducts,
          "Product",
        ),
      },
    };
  }

  function collectDisabledCalendarProductIds(settings) {
    const disabledIds = new Set();
    if (!isRecord(settings?.enforcement)) return disabledIds;

    const disabledProducts = Array.isArray(settings.enforcement.calendarDisabledProducts)
      ? settings.enforcement.calendarDisabledProducts
      : [];

    disabledProducts.forEach((product) => {
      const normalizedId = normalizeTargetId(product?.id, "Product");
      if (normalizedId) disabledIds.add(normalizedId);
    });

    return disabledIds;
  }

  function parsePayload(payload) {
    const data = isRecord(payload) ? payload : {};
    const rawShopConfig = parseJsonValue(data.shopConfig);
    const shopConfig = normalizeSettings(rawShopConfig);
    const cartAttributes = isRecord(data.cartAttributes) ? data.cartAttributes : {};
    const disabledCalendarProductIds = collectDisabledCalendarProductIds(shopConfig);
    const disableCalendarForSelectedProducts = Boolean(
      shopConfig.enforcement?.disableCalendarForSelectedProducts,
    );

    const items = Array.isArray(data.items)
      ? data.items.map((entry) => {
        if (!isRecord(entry)) {
          return {
            productId: "",
            productTitle: "",
            collectionIds: [],
          };
        }

        const productId = normalizeTargetId(entry.productId, "Product");
        const productTitle =
          typeof entry.productTitle === "string" ? entry.productTitle.trim() : "";
        const collectionIds = Array.isArray(entry.collectionIds)
          ? Array.from(
            new Set(
              entry.collectionIds
                .map((id) => normalizeTargetId(id, "Collection"))
                .filter((id) => Boolean(id)),
            ),
          )
          : [];
        const quantity = Number.parseInt(entry.quantity, 10);

        return {
          productId,
          productTitle,
          collectionIds,
          quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        };
      })
      : [];
    const cartTotal = Number.parseInt(data.cartTotal, 10);

    const immediateOnlyItems =
      disableCalendarForSelectedProducts
        ? items.filter((item) => disabledCalendarProductIds.has(item.productId))
        : [];
    const immediateOnlyProductTitles = Array.from(
      new Set(immediateOnlyItems.map((item) => item.productTitle).filter((title) => Boolean(title))),
    );
    const immediateOnlyInCart =
      disableCalendarForSelectedProducts && immediateOnlyItems.length > 0;

    return {
      rawShopConfig,
      shopConfig,
      disabledCalendarProductIds: Array.from(disabledCalendarProductIds),
      disableCalendarForSelectedProducts,
      cartAttributes: {
        deliveryDate: parseDeliveryDateAttribute(cartAttributes.deliveryDate),
        deliveryDay:
          typeof cartAttributes.deliveryDay === "string" ? cartAttributes.deliveryDay.trim() : "",
        deliveryDateFormat:
          typeof cartAttributes.deliveryDateFormat === "string"
            ? cartAttributes.deliveryDateFormat.trim()
            : "",
        deliveryMode:
          cartAttributes.deliveryMode === "scheduled" || cartAttributes.deliveryMode === "asap"
            ? cartAttributes.deliveryMode
            : "",
      },
      items,
      cartTotal: Number.isFinite(cartTotal) ? cartTotal : null,
      immediateOnlyInCart,
      immediateOnlyProductTitles,
    };
  }

  function formatBlockedProductList(productTitles) {
    const titles = Array.isArray(productTitles)
      ? Array.from(
        new Set(
          productTitles
            .map((title) => (typeof title === "string" ? title.trim() : ""))
            .filter((title) => Boolean(title)),
        ),
      )
      : [];

    if (!titles.length) return "items in your cart";
    if (titles.length === 1) return titles[0];
    if (titles.length === 2) return `${titles[0]} and ${titles[1]}`;
    if (titles.length === 3) return `${titles[0]}, ${titles[1]}, and ${titles[2]}`;

    return `${titles[0]}, ${titles[1]}, and ${titles.length - 2} other items`;
  }

  function logCalendarDebug(config, schedule) {
    if (typeof console === "undefined") return;

    const scheduleSummary = {
      nowDateKey: schedule.nowDateKey,
      minDateKey: schedule.minDateKey,
      maxDateKey: schedule.maxDateKey,
      firstAvailableDate: schedule.firstAvailableDate,
      hasAnyAvailableDate: schedule.hasAnyAvailableDate,
      immediateOnlyInCart: schedule.immediateOnlyInCart,
      immediateOnlyProductTitles: Array.isArray(config.immediateOnlyProductTitles)
        ? config.immediateOnlyProductTitles
        : [],
      availableDates: Array.from(schedule.availableSet),
      disableCalendarForSelectedProducts: Boolean(config.disableCalendarForSelectedProducts),
      disabledCalendarProductIds: Array.isArray(config.disabledCalendarProductIds)
        ? config.disabledCalendarProductIds
        : [],
    };

    if (typeof console.groupCollapsed === "function") {
      console.groupCollapsed("[Delivery Calendar] Frontend metafields");
      console.log("Raw shop metafield config", config.rawShopConfig);
      console.log("Normalized shop config", config.shopConfig);
      console.log("Cart attributes", config.cartAttributes);
      console.log("Cart items", config.items);
      console.log("Resolved schedule", scheduleSummary);
      console.groupEnd();
      return;
    }

    console.log("[Delivery Calendar] Raw shop metafield config", config.rawShopConfig);
    console.log("[Delivery Calendar] Normalized shop config", config.shopConfig);
    console.log("[Delivery Calendar] Cart attributes", config.cartAttributes);
    console.log("[Delivery Calendar] Cart items", config.items);
    console.log("[Delivery Calendar] Resolved schedule", scheduleSummary);
  }

  function parseDateKey(dateKey) {
    const normalized = normalizeIsoDate(dateKey);
    if (!normalized) return null;

    const [year, month, day] = normalized
      .split("-")
      .map((entry) => Number.parseInt(entry, 10));

    return { year, month, day };
  }

  function dateKeyToUTCDate(dateKey) {
    const parsed = parseDateKey(dateKey);
    if (!parsed) return null;

    return new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day));
  }

  function addDays(dateKey, days) {
    const parsed = parseDateKey(dateKey);
    if (!parsed) return dateKey;

    const utcDate = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day + days));
    return `${utcDate.getUTCFullYear()}-${pad2(utcDate.getUTCMonth() + 1)}-${pad2(utcDate.getUTCDate())}`;
  }

  function weekdayFromDateKey(dateKey) {
    const utcDate = dateKeyToUTCDate(dateKey);
    if (!utcDate) return "monday";

    return WEEKDAY_KEYS[utcDate.getUTCDay()];
  }

  function weekdayIndexFromDateParts(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  }

  function leadingSlotsForMonth(year, month, firstDayOfWeek) {
    const utcDay = weekdayIndexFromDateParts(year, month, 1);
    const firstDay = normalizeWeekday(firstDayOfWeek, "monday");
    const calendarStartIndex = WEEKDAY_KEYS.indexOf(firstDay);

    if (calendarStartIndex < 0) return utcDay;
    return (utcDay - calendarStartIndex + 7) % 7;
  }

  function monthLabel(year, month) {
    const utcDate = new Date(Date.UTC(year, month - 1, 1));
    const monthName = new Intl.DateTimeFormat("en-GB", {
      month: "long",
      timeZone: "UTC",
    }).format(utcDate);

    const yearValue = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      timeZone: "UTC",
    }).format(utcDate);

    return `${monthName}, ${yearValue}`;
  }

  function monthKey(year, month) {
    return `${year}-${pad2(month)}`;
  }

  function dateKeyMonth(dateKey) {
    return dateKey.slice(0, 7);
  }

  function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
  }

  function createTimezoneParts(date, timezone) {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      weekday: "long",
    });

    const parts = {};
    formatter.formatToParts(date).forEach((part) => {
      if (part.type !== "literal") {
        parts[part.type] = part.value;
      }
    });

    return {
      year: Number.parseInt(parts.year, 10),
      month: Number.parseInt(parts.month, 10),
      day: Number.parseInt(parts.day, 10),
      hour: Number.parseInt(parts.hour, 10),
      minute: Number.parseInt(parts.minute, 10),
      weekday: parts.weekday ? String(parts.weekday).toLowerCase() : "monday",
    };
  }

  function nowInTimezone(timezone) {
    const parts = createTimezoneParts(new Date(), timezone);
    return {
      dateKey: `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`,
      weekday: normalizeWeekday(parts.weekday, "monday"),
      hour: parts.hour,
      minute: parts.minute,
    };
  }

  function isCutoffPassed(nowContext, cutoffTime) {
    const match = cutoffTime.match(TIME_24H_RE);
    if (!match) return false;

    const cutoffHour = Number.parseInt(match[1], 10);
    const cutoffMinute = Number.parseInt(match[2], 10);

    if (nowContext.hour > cutoffHour) return true;
    if (nowContext.hour < cutoffHour) return false;

    return nowContext.minute >= cutoffMinute;
  }

  function resolveMatchedRules(item, availability) {
    if (availability.targetType === "specific_products") {
      if (!item.productId) return [];
      return availability.productRules.filter((rule) => rule.id === item.productId);
    }

    if (!item.collectionIds.length) return [];

    const byId = new Map();
    availability.collectionRules.forEach((rule) => {
      byId.set(rule.id, rule);
    });

    const matched = item.collectionIds
      .map((collectionId) => byId.get(collectionId))
      .filter((rule) => Boolean(rule));

    const dedupedById = new Map();
    matched.forEach((rule) => {
      dedupedById.set(rule.id, rule);
    });

    return Array.from(dedupedById.values());
  }

  function resolveAvailabilitySet(candidateDates, items, availability) {
    const allCandidates = new Set(candidateDates);

    if (!availability.enabled) {
      return allCandidates;
    }

    const lineSets = [];

    items.forEach((item) => {
      const matchedRules = resolveMatchedRules(item, availability);
      if (!matchedRules.length) return;

      const ruleDates = new Set();
      matchedRules.forEach((rule) => {
        rule.dates.forEach((dateKey) => {
          if (allCandidates.has(dateKey)) {
            ruleDates.add(dateKey);
          }
        });
      });

      if (availability.dateMode === "allow") {
        lineSets.push(ruleDates);
        return;
      }

      const lineAllowedSet = new Set();
      candidateDates.forEach((dateKey) => {
        if (!ruleDates.has(dateKey)) {
          lineAllowedSet.add(dateKey);
        }
      });
      lineSets.push(lineAllowedSet);
    });

    if (!lineSets.length) {
      return allCandidates;
    }

    if (availability.cartMatchMode === "any") {
      const union = new Set();
      lineSets.forEach((lineSet) => {
        lineSet.forEach((dateKey) => union.add(dateKey));
      });
      return union;
    }

    const intersection = new Set(candidateDates);
    lineSets.forEach((lineSet) => {
      Array.from(intersection).forEach((dateKey) => {
        if (!lineSet.has(dateKey)) {
          intersection.delete(dateKey);
        }
      });
    });

    return intersection;
  }

  function buildSoldOutDateSet(settings) {
    return new Set(settings.soldOutDates);
  }

  function buildEventDateSet(settings) {
    return new Set(settings.events.map((entry) => entry.date));
  }

  function isBlockedByCalendarRules(dateKey, settings) {
    if (!settings.deliveryWeekdays.includes(weekdayFromDateKey(dateKey))) return true;
    return settings.blockoutDates.includes(dateKey);
  }

  function isUnavailableByGlobalRules(dateKey, settings, soldOutDateSet) {
    if (isBlockedByCalendarRules(dateKey, settings)) return true;
    if (soldOutDateSet.has(dateKey)) return true;
    return false;
  }

  function calculateStartOffset(nowDateKey, minIntervalDays, cutoffShift, settings, soldOutDateSet) {
    const requiredDays = Math.max(0, minIntervalDays + cutoffShift);
    if (requiredDays === 0) return 0;

    let countedDays = 0;
    let offset = 0;
    const hardLimit = settings.maxDeliveryDays + 365;

    while (countedDays < requiredDays && offset <= hardLimit) {
      offset += 1;
      const dateKey = addDays(nowDateKey, offset);
      if (isUnavailableByGlobalRules(dateKey, settings, soldOutDateSet)) {
        continue;
      }
      countedDays += 1;
    }

    return offset;
  }

  function shouldApplyCutoffShift(orderWeekday) {
    return (
      orderWeekday === "monday" ||
      orderWeekday === "tuesday" ||
      orderWeekday === "wednesday" ||
      orderWeekday === "thursday" ||
      orderWeekday === "friday"
    );
  }

  function createLabelsByDate(settings) {
    const labelsByDate = new Map();

    const append = (dateKey, label, tone) => {
      if (!normalizeIsoDate(dateKey) || !label) return;

      if (!labelsByDate.has(dateKey)) {
        labelsByDate.set(dateKey, []);
      }

      const existing = labelsByDate.get(dateKey);
      const dedupeKey = `${label}:${tone}`;
      if (!existing.some((entry) => `${entry.label}:${entry.tone}` === dedupeKey)) {
        existing.push({ label, tone });
      }
    };

    settings.events.forEach((entry) => {
      append(entry.date, entry.label, "event");
    });

    return labelsByDate;
  }

  function resolveSchedule(config) {
    const settings = config.shopConfig;
    const now = nowInTimezone(settings.timezone);
    const orderWeekday = now.weekday;
    const soldOutDateSet = buildSoldOutDateSet(settings);
    const eventDateSet = buildEventDateSet(settings);

    const weekdayMinInterval = clampInt(
      settings.minIntervalByOrderWeekday[orderWeekday],
      1,
      0,
      settings.maxDeliveryDays,
    );

    const cutoffShift =
      settings.cutoff.enabled &&
      shouldApplyCutoffShift(orderWeekday) &&
      isCutoffPassed(now, settings.cutoff.time)
        ? 1
        : 0;

    const displayStartOffset = calculateStartOffset(
      now.dateKey,
      weekdayMinInterval,
      cutoffShift,
      settings,
      new Set(),
    );
    const startOffset = calculateStartOffset(
      now.dateKey,
      weekdayMinInterval,
      cutoffShift,
      settings,
      soldOutDateSet,
    );
    const endOffset = Math.max(startOffset, settings.maxDeliveryDays);

    const candidateDates = [];
    for (let offset = displayStartOffset; offset <= endOffset; offset += 1) {
      const dateKey = addDays(now.dateKey, offset);
      if (isBlockedByCalendarRules(dateKey, settings)) continue;
      candidateDates.push(dateKey);
    }

    const candidateDateSet = new Set(candidateDates);
    const availabilitySet = resolveAvailabilitySet(
      candidateDates,
      config.items,
      settings.availability,
    );

    // Event dates are merchandising dates and should remain selectable when possible.
    eventDateSet.forEach((dateKey) => {
      if (!candidateDateSet.has(dateKey)) return;
      if (soldOutDateSet.has(dateKey)) return;
      availabilitySet.add(dateKey);
    });

    const availableDates = candidateDates.filter(
      (dateKey) => !soldOutDateSet.has(dateKey) && availabilitySet.has(dateKey),
    );

    return {
      nowDateKey: now.dateKey,
      minDateKey: addDays(now.dateKey, displayStartOffset),
      maxDateKey: addDays(now.dateKey, endOffset),
      availableDates,
      availableSet: new Set(availableDates),
      soldOutDateSet,
      labelsByDate: createLabelsByDate(settings),
      firstAvailableDate: availableDates[0] || "",
      hasAnyAvailableDate: availableDates.length > 0,
      cutoffApplied: cutoffShift > 0,
      immediateOnlyInCart: Boolean(config.immediateOnlyInCart),
      immediateOnlyProductTitles: Array.isArray(config.immediateOnlyProductTitles)
        ? config.immediateOnlyProductTitles
        : [],
    };
  }

  function orderedWeekdays(firstDayOfWeek) {
    const normalizedFirstDay = normalizeWeekday(firstDayOfWeek, "monday");
    const startIndex = WEEKDAY_KEYS.indexOf(normalizedFirstDay);
    if (startIndex < 0) return WEEKDAY_KEYS;

    return [...WEEKDAY_KEYS.slice(startIndex), ...WEEKDAY_KEYS.slice(0, startIndex)];
  }

  function withOrdinal(dayNumber) {
    const mod100 = dayNumber % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${dayNumber}th`;

    const mod10 = dayNumber % 10;
    if (mod10 === 1) return `${dayNumber}st`;
    if (mod10 === 2) return `${dayNumber}nd`;
    if (mod10 === 3) return `${dayNumber}rd`;
    return `${dayNumber}th`;
  }

  function formatCalendarDatePart(dateKey, options) {
    const date = dateKeyToUTCDate(dateKey);
    if (!date) return "";

    return new Intl.DateTimeFormat("en-GB", {
      ...options,
      timeZone: "UTC",
    }).format(date);
  }

  function formatDeliveryDateLong(dateKey, _timezone) {
    const parsed = parseDateKey(dateKey);
    if (!parsed) return dateKey;

    const weekday = formatCalendarDatePart(dateKey, { weekday: "long" });
    const month = formatCalendarDatePart(dateKey, { month: "long" });

    return `${weekday} ${withOrdinal(parsed.day)} of ${month}`;
  }

  function formatDeliveryDateShort(dateKey, _timezone) {
    const parsed = parseDateKey(dateKey);
    if (!parsed) return dateKey;

    const month = formatCalendarDatePart(dateKey, { month: "long" });

    return `${withOrdinal(parsed.day)} of ${month}`;
  }

  function formatDeliveryDateAttribute(dateKey) {
    const parsed = parseDateKey(dateKey);
    if (!parsed) return "";

    return `${pad2(parsed.day)}/${pad2(parsed.month)}/${parsed.year}`;
  }

  function formatDeliveryDayAttribute(dateKey) {
    return formatCalendarDatePart(dateKey, { weekday: "long" });
  }

  function parseDeliveryDateAttribute(value) {
    if (typeof value !== "string") return "";

    const trimmed = value.trim();
    const match = trimmed.match(/^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/);
    if (match) {
      return normalizeIsoDate(`${match[3]}-${match[2]}-${match[1]}`) || "";
    }

    return normalizeIsoDate(trimmed) || "";
  }

  function getPersistedSelectionKey(cartAttributes, selectedDate, selectedMode) {
    if (!selectedDate || !selectedMode) return "";

    if (cartAttributes.deliveryDate !== selectedDate) return "";
    if (cartAttributes.deliveryMode !== selectedMode) return "";
    if (!cartAttributes.deliveryDay || !cartAttributes.deliveryDateFormat) return "";

    return `${selectedMode}|${selectedDate}`;
  }

  // Analytics. Calendar behaviour goes out as Shopify customer events (Shopify.analytics.publish), which a
  // custom pixel forwards to GA4 under the visitor's consent, and as Clarity events and tags when Clarity is
  // on the page, so recordings can be filtered by how far away the earliest date was. Tracking never throws.
  let lastTrackedViewKey = "";

  function daysBetweenDateKeys(fromKey, toKey) {
    const from = dateKeyToUTCDate(fromKey);
    const to = dateKeyToUTCDate(toKey);
    if (!from || !to) return null;

    return Math.round((to.getTime() - from.getTime()) / 86400000);
  }

  function publishCalendarEvent(name, params) {
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === "function") {
        analytics.publish(name, params);
      }
    } catch (error) {
      // Analytics must never break the cart.
    }

    try {
      if (typeof window.clarity === "function") {
        window.clarity("event", name);
        ["days_to_earliest", "delivery_mode", "days_ahead"].forEach((key) => {
          const value = params[key];
          if (value === null || value === undefined || value === "") return;
          window.clarity("set", key, String(value));
        });
      }
    } catch (error) {
      // Analytics must never break the cart.
    }
  }

  function calendarContext(config, schedule) {
    const firstAvailable = schedule.firstAvailableDate;
    const titles = Array.from(
      new Set(config.items.map((item) => item.productTitle).filter((title) => Boolean(title))),
    );

    return {
      earliest_date: firstAvailable || "",
      days_to_earliest: firstAvailable ? daysBetweenDateKeys(schedule.nowDateKey, firstAvailable) : null,
      earliest_weekday: firstAvailable ? weekdayFromDateKey(firstAvailable) : "",
      order_weekday: weekdayFromDateKey(schedule.nowDateKey),
      cutoff_passed: Boolean(schedule.cutoffApplied),
      has_dates: schedule.hasAnyAvailableDate,
      calendar_locked: schedule.immediateOnlyInCart,
      item_count: config.items.reduce((total, item) => total + (item.quantity || 1), 0),
      cart_value: config.cartTotal === null ? null : config.cartTotal / 100,
      products: titles.join(", ").slice(0, 100),
    };
  }

  class DeliveryCalendarWidget extends HTMLElement {
    constructor() {
      super();
      const emptyConfig = parsePayload({});

      this.state = {
        config: emptyConfig,
        schedule: resolveSchedule(emptyConfig),
        selectedDate: "",
        selectedMode: "",
        calendarExpanded: false,
        currentMonth: {
          year: 1970,
          month: 1,
        },
      };

      this.boundClickHandler = this.handleClick.bind(this);
      this.persistAbortController = null;
      this.pendingPersistKey = "";
      this.lastPersistedKey = "";
      this.hasRendered = false;
    }

    connectedCallback() {
      this.classList.add("delivery-calendar-root");
      this.renderSkeleton();
      this.addEventListener("click", this.boundClickHandler);

      const configPayload = this.readPayloadFromScript();
      this.init(configPayload);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.boundClickHandler);
      if (this.persistAbortController) {
        this.persistAbortController.abort();
      }
    }

    readPayloadFromScript() {
      const scriptId = this.dataset.configId;
      if (!scriptId) return {};

      const script = document.getElementById(scriptId);
      if (!script) return {};

      try {
        return JSON.parse(script.textContent || "{}");
      } catch (error) {
        console.error("Delivery calendar payload parse failed", error);
        return {};
      }
    }

    init(configPayload) {
      const config = parsePayload(configPayload);
      const schedule = resolveSchedule(config);
      logCalendarDebug(config, schedule);

      const immediateOnly = schedule.immediateOnlyInCart;
      const presetDate = config.cartAttributes.deliveryDate;
      const presetMode = config.cartAttributes.deliveryMode;

      let selectedDate = "";
      let selectedMode = "";
      let calendarExpanded = false;

      if (schedule.firstAvailableDate) {
        selectedDate = schedule.firstAvailableDate;
        selectedMode = "asap";
      }

      if (!immediateOnly && presetMode === "scheduled" && presetDate && schedule.availableSet.has(presetDate)) {
        selectedDate = presetDate;
        selectedMode = "scheduled";
        calendarExpanded = true;
      }

      if (!immediateOnly && presetMode === "asap" && presetDate && schedule.availableSet.has(presetDate)) {
        selectedDate = presetDate;
        selectedMode = "asap";
      }

      if (!schedule.hasAnyAvailableDate) {
        selectedDate = "";
        selectedMode = "";
        calendarExpanded = false;
      }

      if (immediateOnly) {
        selectedMode = selectedDate ? "asap" : "";
        calendarExpanded = false;
      }

      const monthSource = selectedDate || schedule.minDateKey || schedule.nowDateKey;
      const parsedMonth = parseDateKey(monthSource);

      this.state = {
        config,
        schedule,
        selectedDate,
        selectedMode,
        calendarExpanded,
        currentMonth: {
          year: parsedMonth ? parsedMonth.year : 1970,
          month: parsedMonth ? parsedMonth.month : 1,
        },
      };
      this.lastPersistedKey = getPersistedSelectionKey(
        config.cartAttributes,
        selectedDate,
        selectedMode,
      );
      this.pendingPersistKey = "";
      this.hasRendered = false;

      this.syncHiddenInputs();
      this.updateCheckoutState();
      this.render();
      this.emitChange();
      this.persistSelectionToCart();
      this.trackView();
    }

    // One view per distinct calendar state: the cart page re-renders the widget on every line change, and a
    // re-render that shows the same earliest date for the same basket is not a new view.
    trackView() {
      const context = calendarContext(this.state.config, this.state.schedule);
      const viewKey = [
        context.earliest_date,
        context.has_dates,
        context.calendar_locked,
        context.products,
        context.item_count,
      ].join("|");
      if (viewKey === lastTrackedViewKey) return;

      lastTrackedViewKey = viewKey;
      publishCalendarEvent("delivery_calendar_view", context);
    }

    selectionParams() {
      const { config, schedule, selectedDate, selectedMode } = this.state;

      return {
        ...calendarContext(config, schedule),
        delivery_mode: selectedMode || "",
        delivery_date: selectedDate || "",
        days_ahead: selectedDate ? daysBetweenDateKeys(schedule.nowDateKey, selectedDate) : null,
        is_earliest: Boolean(selectedDate) && selectedDate === schedule.firstAvailableDate,
      };
    }

    trackSelection(previousKey) {
      const currentKey = `${this.state.selectedMode}|${this.state.selectedDate}`;
      if (!this.state.selectedDate || currentKey === previousKey) return;

      publishCalendarEvent("delivery_date_select", this.selectionParams());
    }

    refresh(cartPayload) {
      this.init(cartPayload);
    }

    collapseCalendar() {
      if (!this.state.calendarExpanded) return;
      this.state.calendarExpanded = false;
      this.render();
    }

    setValue(dateKey) {
      const normalized = normalizeIsoDate(dateKey);
      if (!normalized) return;
      if (!this.state.schedule.availableSet.has(normalized)) return;
      if (this.state.schedule.immediateOnlyInCart) return;

      this.state.selectedDate = normalized;
      this.state.selectedMode = "scheduled";
      this.state.calendarExpanded = true;

      const parsed = parseDateKey(normalized);
      if (parsed) {
        this.state.currentMonth = {
          year: parsed.year,
          month: parsed.month,
        };
      }

      this.syncHiddenInputs();
      this.updateCheckoutState();
      this.render();
      this.emitChange();
      this.persistSelectionToCart();
    }

    selectAsapMode() {
      if (!this.state.schedule.firstAvailableDate) return;

      this.state.selectedMode = "asap";
      this.state.selectedDate = this.state.schedule.firstAvailableDate;
      this.state.calendarExpanded = false;

      const parsed = parseDateKey(this.state.selectedDate);
      if (parsed) {
        this.state.currentMonth = {
          year: parsed.year,
          month: parsed.month,
        };
      }

      this.syncHiddenInputs();
      this.updateCheckoutState();
      this.render();
      this.emitChange();
      this.persistSelectionToCart();
    }

    selectCalendarMode() {
      if (this.state.schedule.immediateOnlyInCart) return;
      if (!this.state.schedule.hasAnyAvailableDate) return;

      const wasScheduled = this.state.selectedMode === "scheduled";

      this.state.selectedMode = "scheduled";
      if (!wasScheduled || !this.state.schedule.availableSet.has(this.state.selectedDate)) {
        this.state.selectedDate = this.state.schedule.firstAvailableDate;
      }

      if (!this.state.selectedDate) {
        this.state.selectedDate = this.state.schedule.firstAvailableDate;
      }

      this.state.calendarExpanded = true;

      const parsed = parseDateKey(this.state.selectedDate);
      if (parsed) {
        this.state.currentMonth = {
          year: parsed.year,
          month: parsed.month,
        };
      }

      this.syncHiddenInputs();
      this.updateCheckoutState();
      this.render();
      this.emitChange();
    }

    setCalendarMonth(monthDelta) {
      if (!this.state.calendarExpanded) return;

      const nextDate = new Date(
        Date.UTC(this.state.currentMonth.year, this.state.currentMonth.month - 1 + monthDelta, 1),
      );

      const nextMonthKey = monthKey(nextDate.getUTCFullYear(), nextDate.getUTCMonth() + 1);
      const minMonthKey = dateKeyMonth(this.state.schedule.minDateKey);
      const maxMonthKey = dateKeyMonth(this.state.schedule.maxDateKey);

      if (nextMonthKey < minMonthKey || nextMonthKey > maxMonthKey) {
        return;
      }

      this.state.currentMonth = {
        year: nextDate.getUTCFullYear(),
        month: nextDate.getUTCMonth() + 1,
      };

      this.render();
    }

    handleClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const previousSelectionKey = `${this.state.selectedMode}|${this.state.selectedDate}`;

      const modeButton = target.closest("[data-delivery-mode-select]");
      if (modeButton) {
        const mode = modeButton.getAttribute("data-delivery-mode-select");
        if (mode === "asap") {
          this.selectAsapMode();
          this.trackSelection(previousSelectionKey);
          return;
        }
        if (mode === "calendar") {
          const calendarAccordion = this.querySelector("[data-calendar-accordion]");
          const isOpen =
            calendarAccordion instanceof HTMLDetailsElement ? calendarAccordion.open : true;

          if (isOpen) {
            this.selectCalendarMode();
          } else {
            this.collapseCalendar();
          }
          return;
        }
      }

      const dayButton = target.closest("[data-delivery-date]");
      if (dayButton) {
        const dateKey = dayButton.getAttribute("data-delivery-date");
        if (dateKey) {
          this.setValue(dateKey);
          this.trackSelection(previousSelectionKey);
        }
        return;
      }

      const navButton = target.closest("[data-calendar-nav]");
      if (navButton) {
        const direction = navButton.getAttribute("data-calendar-nav");
        this.setCalendarMonth(direction === "prev" ? -1 : 1);
      }
    }

    syncCalendarAccordionState(calendarAccordion, shouldOpenCalendar) {
      if (!(calendarAccordion instanceof HTMLElement)) return;

      if (!this.hasRendered) {
        if ("isOpen" in calendarAccordion) {
          calendarAccordion.isOpen = shouldOpenCalendar;
        }
        calendarAccordion.setAttribute("aria-expanded", shouldOpenCalendar ? "true" : "false");
        if (shouldOpenCalendar) {
          calendarAccordion.setAttribute("open", "");
        } else {
          calendarAccordion.removeAttribute("open");
        }
        calendarAccordion.style.height = "auto";
        calendarAccordion.style.overflow = "visible";
        return;
      }

      if (calendarAccordion.open !== shouldOpenCalendar) {
        calendarAccordion.open = shouldOpenCalendar;
      }
    }

    syncHiddenInputs() {
      const deliveryDateId = this.dataset.deliveryDateInput;
      const deliveryDayId = this.dataset.deliveryDayInput;
      const deliveryDateFormatId = this.dataset.deliveryDateFormatInput;
      const deliveryModeId = this.dataset.deliveryModeInput;

      const deliveryDateInput = deliveryDateId ? document.getElementById(deliveryDateId) : null;
      const deliveryDayInput = deliveryDayId ? document.getElementById(deliveryDayId) : null;
      const deliveryDateFormatInput = deliveryDateFormatId
        ? document.getElementById(deliveryDateFormatId)
        : null;
      const deliveryModeInput = deliveryModeId ? document.getElementById(deliveryModeId) : null;
      const formattedDeliveryDate = this.state.selectedDate
        ? formatDeliveryDateAttribute(this.state.selectedDate)
        : "";
      const deliveryDay = this.state.selectedDate
        ? formatDeliveryDayAttribute(this.state.selectedDate)
        : "";

      if (deliveryDateInput instanceof HTMLInputElement) {
        deliveryDateInput.value = formattedDeliveryDate;
      }

      if (deliveryDayInput instanceof HTMLInputElement) {
        deliveryDayInput.value = deliveryDay;
      }

      if (deliveryDateFormatInput instanceof HTMLInputElement) {
        deliveryDateFormatInput.value = this.state.selectedDate ? "dd/mm/yyyy" : "";
      }

      if (deliveryModeInput instanceof HTMLInputElement) {
        deliveryModeInput.value = this.state.selectedMode || "";
      }
    }

    emitChange() {
      this.dispatchEvent(
        new CustomEvent("delivery-date:change", {
          bubbles: true,
          detail: {
            value: this.state.selectedDate || "",
            mode: this.state.selectedMode || "",
          },
        }),
      );
    }

    persistSelectionToCart() {
      const deliveryDate = this.state.selectedDate || "";
      const deliveryMode = this.state.selectedMode || "";
      if (!deliveryDate || !deliveryMode) return;
      const formattedDeliveryDate = formatDeliveryDateAttribute(deliveryDate);
      const deliveryDay = formatDeliveryDayAttribute(deliveryDate);

      const persistKey = `${deliveryMode}|${deliveryDate}`;
      if (persistKey === this.lastPersistedKey || persistKey === this.pendingPersistKey) {
        return;
      }

      this.pendingPersistKey = persistKey;

      if (this.persistAbortController) {
        this.persistAbortController.abort();
      }

      this.persistAbortController = new AbortController();

      const fallbackHeaders = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      const fetchConfig =
        window.FoxTheme && window.FoxTheme.utils && typeof window.FoxTheme.utils.fetchConfig === "function"
          ? window.FoxTheme.utils.fetchConfig()
          : { method: "POST", headers: fallbackHeaders };

      const config = {
        ...fetchConfig,
        method: fetchConfig.method || "POST",
        headers: fetchConfig.headers || fallbackHeaders,
        body: JSON.stringify({
          attributes: {
            "Delivery Date": formattedDeliveryDate,
            "Delivery Day": deliveryDay,
            "Date Format": "dd/mm/yyyy",
            "Delivery Mode": deliveryMode,
          },
        }),
        signal: this.persistAbortController.signal,
      };

      const cartUpdateUrl =
        window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_update_url
          ? window.FoxTheme.routes.cart_update_url
          : "/cart/update.js";

      fetch(cartUpdateUrl, config)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to persist delivery selection (${response.status})`);
          }
          return response.json();
        })
        .then(() => {
          if (this.pendingPersistKey !== persistKey) return;
          this.lastPersistedKey = persistKey;
          this.pendingPersistKey = "";
        })
        .catch((error) => {
          if (error && error.name === "AbortError") return;
          this.pendingPersistKey = "";
          console.error("Delivery selection persist failed", error);
        });
    }

    updateCheckoutState() {
      const checkoutButtonId = this.dataset.checkoutButtonId;
      const checkoutButton = checkoutButtonId
        ? document.getElementById(checkoutButtonId)
        : this.closest("main-cart")?.querySelector('[name="checkout"]');

      const additionalButtons = this.closest("main-cart")?.querySelector(
        ".additional-checkout-buttons",
      );

      const shouldPrevent = !this.state.selectedDate;

      if (checkoutButton instanceof HTMLButtonElement) {
        checkoutButton.disabled = shouldPrevent;
        checkoutButton.classList.toggle("is-disabled", shouldPrevent);
      }

      if (additionalButtons instanceof HTMLElement) {
        additionalButtons.classList.toggle("delivery-calendar-disabled", shouldPrevent);
      }
    }

    renderSkeleton() {
      this.innerHTML = `
        <section class="delivery-calendar-widget">
          <h3 class="delivery-calendar-widget__question">When should your order arrive?</h3>
          <div class="delivery-calendar-widget__mode-list">
            <button type="button" class="delivery-calendar-widget__mode-card" data-delivery-mode-select="asap">
              <span class="delivery-calendar-widget__mode-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-truck-icon lucide-truck"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
              </span>
              <span class="delivery-calendar-widget__mode-content">
                <span class="delivery-calendar-widget__mode-title">As soon as possible</span>
                <span class="delivery-calendar-widget__mode-subtitle" data-asap-subtitle></span>
              </span>
            </button>

            <details
              is="accordion-details"
              class="accordion-details delivery-calendar-widget__calendar-accordion"
              data-calendar-accordion
            >
              <summary
                class="accordion-details__summary delivery-calendar-widget__mode-card"
                data-delivery-mode-select="calendar"
              >
                <span class="delivery-calendar-widget__mode-icon" aria-hidden="true">
                 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-calendar-fold-icon lucide-calendar-fold"><path d="M3 20a2 2 0 0 0 2 2h10a2.4 2.4 0 0 0 1.706-.706l3.588-3.588A2.4 2.4 0 0 0 21 16V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z"/><path d="M15 22v-5a1 1 0 0 1 1-1h5"/><path d="M8 2v4"/><path d="M16 2v4"/><path d="M3 10h18"/></svg>
                </span>
                <span class="delivery-calendar-widget__mode-content">
                  <span class="delivery-calendar-widget__mode-title">Select a date</span>
                  <span class="delivery-calendar-widget__mode-subtitle" data-calendar-subtitle></span>
                </span>
                <span class="delivery-calendar-widget__caret" data-calendar-caret aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.9 7.2L10 13.1 4.1 7.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </summary>

              <div class="accordion-details__content delivery-calendar-widget__calendar-shell" data-calendar-shell>
                <div class="delivery-calendar-widget__calendar-nav">
                  <button class="delivery-calendar-widget__calendar-nav-btn" type="button" data-calendar-nav="prev" aria-label="Previous month">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M13.5 6.5L8 12l5.5 5.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M8.5 12h11.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
                    </svg>
                  </button>
                  <p class="delivery-calendar-widget__calendar-month" data-calendar-month></p>
                  <button class="delivery-calendar-widget__calendar-nav-btn" type="button" data-calendar-nav="next" aria-label="Next month">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M10.5 6.5 16 12l-5.5 5.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M15.5 12H4" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
                <div class="delivery-calendar-widget__calendar-body" data-calendar-body></div>
                <p class="delivery-calendar-widget__reason" data-calendar-reason hidden></p>
                <div class="delivery-calendar-widget__notes" data-calendar-notes hidden></div>
                <footer class="delivery-calendar-widget__legend" data-calendar-legend hidden></footer>
              </div>
            </details>
          </div>
        </section>
      `;
    }

    render() {
      const widget = this.querySelector(".delivery-calendar-widget");
      const asapButton = this.querySelector('[data-delivery-mode-select="asap"]');
      const calendarButton = this.querySelector('[data-delivery-mode-select="calendar"]');
      const calendarAccordion = this.querySelector("[data-calendar-accordion]");
      const asapSubtitle = this.querySelector("[data-asap-subtitle]");
      const calendarSubtitle = this.querySelector("[data-calendar-subtitle]");
      const caret = this.querySelector("[data-calendar-caret]");

      const monthLabelEl = this.querySelector("[data-calendar-month]");
      const calendarBodyEl = this.querySelector("[data-calendar-body]");
      const reasonEl = this.querySelector("[data-calendar-reason]");
      const notesEl = this.querySelector("[data-calendar-notes]");
      const legendEl = this.querySelector("[data-calendar-legend]");

      if (
        !widget ||
        !asapButton ||
        !calendarButton ||
        !calendarAccordion ||
        !asapSubtitle ||
        !calendarSubtitle ||
        !caret ||
        !monthLabelEl ||
        !calendarBodyEl ||
        !reasonEl ||
        !notesEl ||
        !legendEl
      ) {
        return;
      }

      const settings = this.state.config.shopConfig;
      const schedule = this.state.schedule;
      const firstAvailable = schedule.firstAvailableDate;
      const selectedDate = this.state.selectedDate;

      widget.setAttribute("data-alignment", settings.ui.calendarAlignment || "center");
      widget.style.setProperty("--dc-selected-color", settings.ui.selectedDateColor || "#fa1b3c");
      widget.style.setProperty("--dc-available-color", settings.ui.availableDateColor || "#ffe9f5");
      widget.style.setProperty("--dc-soldout-color", settings.ui.soldOutDateColor || "#D9D9D9");
      widget.style.setProperty("--dc-event-color", settings.ui.eventTagColor || "#fffdc2");
      widget.style.setProperty(
        "--dc-unavailable-color",
        settings.ui.unavailableDateColor || "#F3F3F3",
      );
      widget.style.setProperty("--dc-today-dot-color", settings.ui.todayDotColor || "#7f0016");

      const noteParagraphs = splitCalendarNotes(settings.notesText);
      if (noteParagraphs.length) {
        notesEl.hidden = false;
        notesEl.innerHTML = noteParagraphs
          .map(
            (paragraph) =>
              `<p class="delivery-calendar-widget__notice">${escapeHtml(paragraph)}</p>`,
          )
          .join("");
      } else {
        notesEl.hidden = true;
        notesEl.innerHTML = "";
      }

      if (firstAvailable) {
        asapSubtitle.innerHTML = `Tracked delivery <strong>${escapeHtml(formatDeliveryDateLong(firstAvailable, settings.timezone))}</strong>`;
      } else {
        asapSubtitle.textContent = "No valid delivery date available.";
      }

      if (schedule.immediateOnlyInCart) {
        calendarSubtitle.textContent = `Not available for ${formatBlockedProductList(
          schedule.immediateOnlyProductTitles,
        )}`;
      } else if (this.state.selectedMode === "scheduled" && selectedDate) {
        calendarSubtitle.innerHTML = `Delivery on <strong>${escapeHtml(formatDeliveryDateLong(selectedDate, settings.timezone))}</strong>`;
      } else if (firstAvailable) {
        calendarSubtitle.innerHTML = `Delivery from <strong>${escapeHtml(formatDeliveryDateShort(firstAvailable, settings.timezone))}</strong>`;
      } else {
        calendarSubtitle.textContent = "No date available.";
      }

      if (schedule.immediateOnlyInCart || !schedule.hasAnyAvailableDate) {
        calendarButton.classList.add("is-disabled");
        calendarButton.setAttribute("aria-disabled", "true");
        caret.hidden = true;
      } else {
        calendarButton.classList.remove("is-disabled");
        calendarButton.removeAttribute("aria-disabled");
        caret.hidden = false;
      }

      const shouldOpenCalendar =
        !schedule.immediateOnlyInCart && this.state.calendarExpanded && schedule.hasAnyAvailableDate;

      const asapSelected =
        !shouldOpenCalendar &&
        (this.state.selectedMode === "asap" || (!this.state.selectedMode && firstAvailable));
      const calendarSelected = shouldOpenCalendar || this.state.selectedMode === "scheduled";

      asapButton.classList.toggle("is-active", Boolean(asapSelected));
      calendarButton.classList.toggle("is-active", Boolean(calendarSelected));

      calendarButton.classList.toggle("is-open", shouldOpenCalendar);
      caret.classList.toggle("is-open", shouldOpenCalendar);
      this.syncCalendarAccordionState(calendarAccordion, shouldOpenCalendar);

      if (!shouldOpenCalendar) {
        this.hasRendered = true;
        return;
      }

      const weekdayOrder = orderedWeekdays(settings.firstDayOfWeek);
      const { year, month } = this.state.currentMonth;

      monthLabelEl.textContent = monthLabel(year, month);

      const leadingSlots = leadingSlotsForMonth(year, month, settings.firstDayOfWeek);
      const totalDays = daysInMonth(year, month);

      const cells = [];
      weekdayOrder.forEach((weekday) => {
        cells.push(`<span class="delivery-calendar-widget__weekday">${WEEKDAY_LABELS[weekday]}</span>`);
      });

      for (let day = 1; day <= totalDays; day += 1) {
        const dateKey = `${year}-${pad2(month)}-${pad2(day)}`;
        const columnStartStyle =
          day === 1 && leadingSlots > 0 ? ` style="grid-column-start: ${leadingSlots + 1};"` : "";

        const selectable = schedule.availableSet.has(dateKey);
        const selected = selectedDate === dateKey;
        const isToday = schedule.nowDateKey === dateKey;

        const inWindow = dateKey >= schedule.minDateKey && dateKey <= schedule.maxDateKey;
        const labels = schedule.labelsByDate.get(dateKey) || [];

        const soldOut =
          inWindow &&
          (schedule.soldOutDateSet.has(dateKey) ||
            labels.some((entry) => entry.tone === "sold_out"));
        const eventLabel = labels.find((entry) => entry.tone !== "sold_out");

        const classes = [
          "delivery-calendar-widget__day",
          selectable ? "is-selectable" : "is-unavailable",
          selected ? "is-selected" : "",
          soldOut ? "is-sold-out" : "",
          soldOut && !selected ? "is-tag-only" : "",
          isToday ? "is-today" : "",
          !inWindow ? "is-outside-window" : "",
        ]
          .filter((entry) => Boolean(entry))
          .join(" ");

        let tagHtml = "";
        if (soldOut) {
          tagHtml = '<span class="delivery-calendar-widget__day-tag is-sold-out">SOLD OUT</span>';
        } else if (eventLabel) {
          tagHtml = `<span class="delivery-calendar-widget__day-tag is-event">${escapeHtml(eventLabel.label)}</span>`;
        }

        cells.push(`
          <button
            type="button"
            class="${classes}"
            data-delivery-date="${dateKey}"
            ${columnStartStyle}
            ${selectable ? "" : "disabled"}
            aria-pressed="${selected ? "true" : "false"}"
          >
            <span class="delivery-calendar-widget__day-number">${day}</span>
            ${isToday ? '<span class="delivery-calendar-widget__today-dot"></span>' : ""}
            ${tagHtml}
          </button>
        `);
      }

      calendarBodyEl.innerHTML = cells.join("");

      if (!schedule.hasAnyAvailableDate) {
        reasonEl.hidden = false;
        reasonEl.textContent =
          "Checkout is blocked because no valid delivery date is available in the configured window.";
      } else {
        reasonEl.hidden = true;
        reasonEl.textContent = "";
      }

      if (settings.ui.showLegend) {
        const hasSoldOutDateInDisplayedRange = Array.from(schedule.soldOutDateSet).some(
          (dateKey) => dateKey >= schedule.minDateKey && dateKey <= schedule.maxDateKey,
        );
        const legendItems = [
          { key: "selected", label: "Selected", color: settings.ui.selectedDateColor || "#fa1b3c" },
          { key: "available", label: "Available", color: settings.ui.availableDateColor || "#ffe9f5" },
          {
            key: "unavailable",
            label: "Unavailable",
            color: settings.ui.unavailableDateColor || "#F3F3F3",
          },
        ];

        if (hasSoldOutDateInDisplayedRange) {
          legendItems.splice(2, 0, {
            key: "sold_out",
            label: "Sold out",
            color: settings.ui.soldOutDateColor || "#D9D9D9",
          });
        }

        legendEl.hidden = false;
        legendEl.innerHTML = legendItems
          .map(
            (item) => `
              <span class="delivery-calendar-widget__legend-chip" data-key="${escapeHtml(item.key)}" style="--legend-color: ${escapeHtml(item.color)};">
                <span>${escapeHtml(item.label)}</span>
              </span>
            `,
          )
          .join("");
      } else {
        legendEl.hidden = true;
        legendEl.innerHTML = "";
      }

      const minMonth = dateKeyMonth(schedule.minDateKey);
      const maxMonth = dateKeyMonth(schedule.maxDateKey);
      const currentMonth = monthKey(year, month);

      const prevButton = this.querySelector('[data-calendar-nav="prev"]');
      const nextButton = this.querySelector('[data-calendar-nav="next"]');

      if (prevButton instanceof HTMLButtonElement) {
        prevButton.disabled = currentMonth <= minMonth;
      }

      if (nextButton instanceof HTMLButtonElement) {
        nextButton.disabled = currentMonth >= maxMonth;
      }

      this.hasRendered = true;
    }
  }

  if (!customElements.get("delivery-calendar-widget")) {
    customElements.define("delivery-calendar-widget", DeliveryCalendarWidget);
  }

  // Checkout from the cart page is a native form submit with the "checkout" submitter. Capture phase, so the
  // event goes out before the browser leaves for checkout.
  if (!window.__deliveryCalendarCheckoutTracking) {
    window.__deliveryCalendarCheckoutTracking = true;
    document.addEventListener(
      "submit",
      (event) => {
        const submitter = event.submitter;
        if (!submitter || submitter.getAttribute("name") !== "checkout") return;

        const form = event.target instanceof HTMLFormElement ? event.target : null;
        const widget =
          (form && form.querySelector("delivery-calendar-widget")) ||
          document.querySelector("delivery-calendar-widget");
        if (!widget || typeof widget.selectionParams !== "function") return;

        publishCalendarEvent("delivery_checkout_click", widget.selectionParams());
      },
      true,
    );
  }
})();
